/**
 * Custom-domain compatibility shim (added 2026-02-08).
 *
 * Problem: production was deployed with `REACT_APP_BACKEND_URL` pointing at
 * the BARE apex domain (`https://annadilburhanidar.bluedigital.co.tz`) which
 * has no valid TLS certificate. Every axios call from the production
 * frontend therefore fails with a silent TLS handshake error — including
 * the post-Google-login session-exchange POST, which makes admins appear
 * "not signed in".
 *
 * Fix: if the page is served from a host that DIFFERS from
 * `REACT_APP_BACKEND_URL`'s host (i.e. the user is on a custom domain),
 * rewrite all API calls to use the page's own origin. The Emergent ingress
 * already routes `/api/*` to the FastAPI service on the same host, so
 * this is fully equivalent to the working setup — just routed via the
 * domain that actually has a TLS cert.
 *
 * This is a no-op in the preview environment (REACT_APP_BACKEND_URL matches
 * the page host).
 */
import axios from "axios";

const ENV_BASE = process.env.REACT_APP_BACKEND_URL || "";

function shouldRewriteToOrigin() {
  if (typeof window === "undefined") return false;
  if (!ENV_BASE) return false;
  try {
    const envHost = new URL(ENV_BASE).host;
    const pageHost = window.location.host;
    // Rewrite when the configured backend host differs from the page host
    // AND the page host is NOT the emergentagent preview (preview hosts must
    // call the explicit REACT_APP_BACKEND_URL).
    if (envHost === pageHost) return false;
    if (pageHost.includes(".emergentagent.com")) return false;
    return true;
  } catch {
    return false;
  }
}

const REWRITE = shouldRewriteToOrigin();
const PAGE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

/**
 * Public helper — returns the effective API base URL for the current page.
 * Use this anywhere you need the host (e.g. for sharing direct media URLs).
 */
export function apiBase() {
  if (REWRITE) return PAGE_ORIGIN;
  return ENV_BASE;
}

let installed = false;
export function installApiBaseShim() {
  if (installed || !REWRITE) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    try {
      if (!config.url) return config;
      // Only rewrite absolute URLs that point at the misconfigured host
      if (config.url.startsWith(ENV_BASE)) {
        config.url = PAGE_ORIGIN + config.url.slice(ENV_BASE.length);
      }
    } catch {
      // Never break a request because of a URL-shim error
    }
    return config;
  });
  // Surface it once in the console so the issue can be diagnosed from devtools
  // eslint-disable-next-line no-console
  console.info(`[api-base shim] Rewriting ${ENV_BASE} → ${PAGE_ORIGIN}`);
}
